import { Card, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid2";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";

interface Percentage {
  color:
    | "inherit"
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "light"
    | "dark"
    | "text"
    | "white";
  value: string | number;
  label: string;
}

interface Dropdown {
  action: (event: React.MouseEvent<HTMLElement>) => void;
  menu: React.ReactNode;
  value: string;
}

interface DefaultStatisticsCardProps {
  title: string;
  count: string | number;
  percentage?: Percentage;
  dropdown?: boolean | Dropdown;
  errorInfo?: {
    shouldLoadError: boolean;
    title: string;
    description: string;
    alt: string;
  };
  isLoading?: boolean;
}

const DefaultStatisticsCard: React.FC<DefaultStatisticsCardProps> = ({
  title,
  count,
  percentage = { color: "success", value: "", label: "" },
  dropdown = false,
  errorInfo = { shouldLoadError: false, title: "", description: "", alt: "" },
  isLoading = false,
}) => {
  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={2}>
        {errorInfo.shouldLoadError && (
          <AlertBlock
            title={errorInfo.title}
            description={errorInfo.description}
            alt={errorInfo.alt}
          />
        )}
        {!errorInfo.shouldLoadError && (
          <Grid container>
            <Grid size={7}>
              <MDBox mb={0.5} lineHeight={1}>
                {isLoading ? (
                  <Skeleton variant="text" width={100} height={20} />
                ) : (
                  <MDTypography variant="button" fontWeight="medium" color="text">
                    {title}
                  </MDTypography>
                )}
              </MDBox>
              <MDBox lineHeight={1}>
                {isLoading ? (
                  <Skeleton variant="text" width={60} height={30} />
                ) : (
                  <MDTypography variant="h5" fontWeight="bold">
                    {count}
                  </MDTypography>
                )}

                {isLoading ? (
                  <Skeleton variant="text" width={50} height={20} />
                ) : (
                  <>
                    <MDTypography variant="button" fontWeight="bold" color={percentage.color}>
                      {percentage.value === "undefined%" ? "0%" : percentage.value}&nbsp;
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="regular" color={"text"}>
                      {percentage.label}
                    </MDTypography>
                  </>
                )}
              </MDBox>
            </Grid>
            <Grid size={5}>
              {dropdown && typeof dropdown !== "boolean" && (
                <MDBox width="100%" textAlign="right" lineHeight={1}>
                  {isLoading ? (
                    <Skeleton variant="text" width={60} height={20} />
                  ) : (
                    <MDTypography
                      variant="caption"
                      fontWeight="regular"
                      sx={{ cursor: "pointer" }}
                      onClick={dropdown.action}
                    >
                      {dropdown.value}
                    </MDTypography>
                  )}
                  {!isLoading && dropdown.menu}
                </MDBox>
              )}
            </Grid>
          </Grid>
        )}
      </MDBox>
    </Card>
  );
};

export default DefaultStatisticsCard;
